<template>
  <div id="projects" class="relative w-full bg-gray-900 text-white font-sans overflow-hidden py-32">
    <h2 class="text-4xl font-bold text-center mb-10">Our Project Portfolio</h2>
    <div class="py-12">
      <!-- Carousel Track with touch events for swiping -->
      <div 
        ref="carouselTrack"
        class="flex items-center justify-start transform transition-transform duration-300 cursor-grab active:cursor-grabbing"
        style="-ms-overflow-style: none; scrollbar-width: none;"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd">
        
        <div 
          v-for="(movie, index) in movies" 
          :key="index"
          ref="cardRefs"
          class="flex-none rounded-xl shadow-lg transform transition-all duration-300 cursor-pointer"
          :class="{ 'opacity-100 scale-120 z-10 p-4': index === currentIndex, 'opacity-50 scale-90 z-0 p-3': index !== currentIndex }"
          @click="selectCard(index)">
          
          <a :href="movie.url" target="_blank" rel="noopener noreferrer">
            <div class="relative w-full rounded-xl overflow-hidden shadow-lg">
              <img :src="movie.imageUrl" :alt="movie.title" class="w-full h-full object-cover rounded-xl"/>
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-6">
                <div class="text-xl font-bold mb-1">{{ movie.title }}</div>
                <div class="text-sm text-gray-300">{{ movie.description }}</div>
              </div>
            </div>
          </a>
        </div>
      </div>
      
      <!-- Navigation Buttons -->
      <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none w-full px-4">
        <div class="flex justify-between w-full">
          <button @click="prevSlide"
                  class="p-3 rounded-full bg-white/10 text-white shadow-lg transition-colors hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button @click="nextSlide"
                  class="p-3 rounded-full bg-white/10 text-white shadow-lg transition-colors hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import winmetalImage from '../assets/winmetal.png';
import red from '../assets/red.png';
import nine from '../assets/nine.png';
import promotive from '../assets/promotive.png';
import clover from '../assets/clover.png';
import flexi from '../assets/flexi.png'
import swimbuild from '../assets/swimbuild.png'

const movies = ref([
  { title: 'Win Metal Industrial Corporation', description: 'Industrial Metal Distributor', imageUrl: winmetalImage, url: 'https://www.winmetalindustrial.com/' },
  { title: 'Red Entertainment Rentals', description: 'Event Lighting and Sound System Distributor', imageUrl: red, url: 'https://redentertainment-rentals.com' },
  { title: 'NinetyNine Resorts PH', description: 'A resort conglomerate business.', imageUrl: nine, url: 'https://ninetynineresortsph.com' },
  { title: 'Swimbuild Construction', description: 'A swimming pool construction company', imageUrl: swimbuild, url: 'https://www.swimbuildconstruction.com/' },
  { title: 'Promotive Lubricants', description: 'An upstart business of oil and coolant materials', imageUrl: promotive, url: 'https://promotivelubricants.com' },
  { title: 'Clover Skincare PH', description: 'An e-commerce site for skincare products', imageUrl: clover, url: 'https://cloverskincareph.com' },
  { title: 'FlexiCore USA', description: 'A Contractor for expert construction services', imageUrl: flexi, url: 'https://flexicoreinc.com' },
]);

// Clone the first and last few items to create the endless loop effect
const originalMoviesLength = movies.value.length;
movies.value = [
  ...movies.value.slice(-3),
  ...movies.value,
  ...movies.value.slice(0, 3)
];

const currentIndex = ref(3);
const cardRefs = ref([]);
const carouselTrack = ref(null);
const isTransitioning = ref(false);

const updateCarousel = async () => {
  await nextTick();
  if (cardRefs.value.length > 0) {
    const activeCard = cardRefs.value[currentIndex.value];
    const container = carouselTrack.value;
    
    const activeCardCenter = activeCard.offsetLeft + activeCard.offsetWidth / 2;
    const containerCenter = container.offsetWidth / 2;
    const transformX = containerCenter - activeCardCenter;

    container.style.transform = `translateX(${transformX}px)`;
  }
};

const handleTransitionEnd = () => {
  if (isTransitioning.value) {
    // We have reached a cloned slide, now reset to the original without a visible transition
    if (currentIndex.value >= originalMoviesLength + 3) {
      carouselTrack.value.style.transitionDuration = '0s';
      currentIndex.value = 3;
      updateCarousel();
    } else if (currentIndex.value < 3) {
      carouselTrack.value.style.transitionDuration = '0s';
      currentIndex.value = originalMoviesLength + 2;
      updateCarousel();
    }
    isTransitioning.value = false;
  }
};

const nextSlide = () => {
  if (!isTransitioning.value) {
    isTransitioning.value = true;
    currentIndex.value++;
    carouselTrack.value.style.transitionDuration = '300ms';
    updateCarousel();
  }
};

const prevSlide = () => {
  if (!isTransitioning.value) {
    isTransitioning.value = true;
    currentIndex.value--;
    carouselTrack.value.style.transitionDuration = '300ms';
    updateCarousel();
  }
};

const selectCard = (index) => {
    isTransitioning.value = true;
    currentIndex.value = index;
    carouselTrack.value.style.transitionDuration = '300ms';
    updateCarousel();
};

// Swiping functionality
const startX = ref(0);
const currentX = ref(0);
const isSwiping = ref(false);

const handleTouchStart = (e) => {
  isSwiping.value = true;
  startX.value = e.touches[0].clientX;
  carouselTrack.value.style.transitionDuration = '0s';
};

const handleTouchMove = (e) => {
  if (!isSwiping.value) return;
  currentX.value = e.touches[0].clientX;
  const deltaX = currentX.value - startX.value;
  // Apply the transform to the parent to allow for real-time dragging
  carouselTrack.value.style.transform = `translateX(${deltaX}px)`;
};

const handleTouchEnd = () => {
  if (!isSwiping.value) return;
  isSwiping.value = false;

  const swipeThreshold = 50; // pixels
  const deltaX = currentX.value - startX.value;

  carouselTrack.value.style.transitionDuration = '300ms';

  if (deltaX > swipeThreshold) {
    prevSlide();
  } else if (deltaX < -swipeThreshold) {
    nextSlide();
  } else {
    updateCarousel();
  }
};

onMounted(() => {
  // Use a timeout to ensure all elements have been rendered and measured
  setTimeout(() => {
    updateCarousel();
  }, 0);
  window.addEventListener('resize', updateCarousel);
  if (carouselTrack.value) {
    carouselTrack.value.addEventListener('transitionend', handleTransitionEnd);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateCarousel);
  if (carouselTrack.value) {
    carouselTrack.value.removeEventListener('transitionend', handleTransitionEnd);
  }
});
</script>

<style scoped>
/* Hide scrollbar for different browsers */
.overflow-hidden::-webkit-scrollbar {
  display: none;
}
</style>
